import React, { useEffect, useState } from 'react';
import {
  Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControl, IconButton, InputLabel, MenuItem, Select, Stack,
  TextField, Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ApiError, processesApi } from '../../../../api';
import { getFieldFamilyLabel } from '../helpers';
import { translateApiError } from '../../../../i18n/translateApiError';
import type { BaseFormComponent } from '../types';

interface CreateComponentDialogProps {
  open: boolean;
  component?: BaseFormComponent | null;
  onClose: () => void;
  onSaved: () => void;
}

const FIELD_FAMILIES = ['Basic', 'Selection', 'Financial', 'Media', 'HR', 'Special', 'Advanced', 'Business'];
const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;
const GENERAL_ACCEPT = 'application/pdf,image/jpeg,image/png,.pdf,.jpg,.jpeg,.png';
const INVOICE_ACCEPT = 'application/pdf,.pdf';

/** Every type an admin may build a custom component from.
 *
 * MEASURED AGAINST THE BACKEND (2026-09-01): `form_validation.SUPPORTED_FORM_FIELD_TYPES` has 14
 * entries and this list offered 9, so six types could be dropped onto a form from the seeded library
 * but never created as the tenant's own. `multi_select` was the one that mattered — "our departments,
 * pick several" was unbuildable, which makes the type UNUSABLE rather than merely inflexible.
 *
 * `invoice_file` is the one entry with no backend type: it is `file_attachment` with a fixed preset
 * (`document_kind: 'invoice'`), which is why it stays here and why `componentTypeParity.test`
 * exempts it by name rather than by loosening the comparison.
 *
 * The parity test derives the rest from the backend source, so a type added there fails a test
 * instead of quietly staying uncreatable. */
export const COMPONENT_TYPE_OPTIONS = [
  'text_field', 'textarea_field', 'number_field', 'currency_field',
  'date_picker', 'select_dropdown', 'multi_select', 'organization_location',
  'place_field', 'worker_reference', 'department_reference',
  'duration_field', 'static_text',
  'file_attachment', 'invoice_file',
] as const;
export type ComponentTypeOption = (typeof COMPONENT_TYPE_OPTIONS)[number];

const MAX_COMPONENT_NAME_LENGTH = 64; // Safely below the backend String(100) limit.

function componentNameHash(value: string): string {
  let fnv = 0x811c9dc5;
  let mixed = 0x9e3779b9;
  Array.from(value).forEach((character) => {
    const codePoint = character.codePointAt(0) ?? 0;
    // Hash every byte so supplementary code points do not collapse into a
    // single XOR operation. The second independently mixed lane backs up FNV.
    for (let shift = 0; shift < 32; shift += 8) {
      const byte = (codePoint >>> shift) & 0xff;
      fnv = Math.imul(fnv ^ byte, 0x01000193) >>> 0;
      mixed = Math.imul((mixed ^ byte) + 0x7ed55d16, 0x85ebca6b) >>> 0;
      mixed = (mixed ^ (mixed >>> 13)) >>> 0;
    }
  });
  return `${fnv.toString(16).padStart(8, '0')}${(mixed >>> 0).toString(16).padStart(8, '0')}`;
}

/** Tenant-owned stable slug. Runtime behavior is stored separately in field_type. */
export const deriveComponentName = (label: string): string => {
  const normalized = label.trim().toLowerCase().normalize('NFKD');
  const rawAsciiSlug = normalized.replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  const asciiSlug = rawAsciiSlug && !/^[a-z]/.test(rawAsciiSlug)
    ? `f_${rawAsciiSlug}`
    : rawAsciiSlug;
  const nonAsciiCodePoints = Array.from(normalized)
    .map((character) => character.codePointAt(0) ?? 0)
    .filter((codePoint) => codePoint > 0x7f)
    .map((codePoint) => codePoint.toString(16));
  if (nonAsciiCodePoints.length === 0) {
    const bounded = asciiSlug.slice(0, MAX_COMPONENT_NAME_LENGTH) || 'custom_field';
    return bounded.length >= 2 ? bounded : `${bounded}_`;
  }

  // Keep the distinguishing Unicode code points visible. If a long label makes
  // that readable section truncate, the independent 64-bit suffix remains the
  // deterministic backstop. The ASCII prefix receives only the remaining budget.
  const hash = componentNameHash(normalized);
  const readable = nonAsciiCodePoints.join('_');
  const readableBudget = MAX_COMPONENT_NAME_LENGTH - 'u__'.length - hash.length;
  const boundedReadable = readable.slice(0, readableBudget).replace(/_+$/g, '');
  const suffix = `u_${boundedReadable}_${hash}`;
  const prefixBudget = MAX_COMPONENT_NAME_LENGTH - suffix.length - 1;
  const boundedPrefix = asciiSlug.slice(0, Math.max(0, prefixBudget)).replace(/_+$/g, '');
  return boundedPrefix ? `${boundedPrefix}_${suffix}` : suffix;
};

export function runtimeSelection(component: BaseFormComponent | null | undefined): ComponentTypeOption {
  if (component?.fieldType === 'file_attachment' && component.runtimeProperties.document_kind === 'invoice') {
    return 'invoice_file';
  }
  return COMPONENT_TYPE_OPTIONS.includes(component?.fieldType as ComponentTypeOption)
    ? component?.fieldType as ComponentTypeOption
    : 'text_field';
}

export function controlledProperties(
  selection: ComponentTypeOption,
  optionsText: string,
  currency: string,
): Record<string, unknown> {
  if (selection === 'invoice_file') return {
    document_kind: 'invoice', accept: INVOICE_ACCEPT, max_count: 1,
    max_bytes: MAX_ATTACHMENT_BYTES, multiple: false,
  };
  if (selection === 'file_attachment') return {
    accept: GENERAL_ACCEPT, max_count: 1, max_bytes: MAX_ATTACHMENT_BYTES, multiple: false,
  };
  // `multi_select` is `select_dropdown` with a list VALUE; the choices are declared identically, so
  // they share this branch rather than getting a second copy of the parser (svdy-rules §10.5).
  if (selection === 'select_dropdown' || selection === 'multi_select') return {
    options: optionsText.split('\n').map((value) => value.trim()).filter(Boolean),
  };
  if (selection === 'currency_field') return { currency: currency.trim().toUpperCase() || 'USD' };
  return {};
}

const CONTROLLED_PROPERTY_KEYS = [
  'document_kind', 'accept', 'max_count', 'max_bytes', 'multiple', 'options', 'currency',
] as const;

interface ComponentEditorValues {
  name: string;
  label: string;
  category: string;
  description: string;
  selection: ComponentTypeOption;
  optionsText: string;
  currency: string;
}

/** Build the backend PATCH contract without replacing values this dialog never edits. */
export function buildComponentUpdatePayload(
  component: BaseFormComponent,
  values: ComponentEditorValues,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  const hasLegacyPropertyDescriptors = Array.isArray(component.sourceProperties);
  const fieldType = values.selection === 'invoice_file' ? 'file_attachment' : values.selection;
  const description = values.description.trim() || null;
  if (values.name !== component.name) payload.name = values.name;
  if (!hasLegacyPropertyDescriptors && fieldType !== component.fieldType) payload.field_type = fieldType;
  if (values.label !== component.label) payload.label = values.label;
  if (values.category !== component.category) payload.category = values.category;
  if (description !== (component.description || null)) payload.description = description;

  const initialSelection = runtimeSelection(component);
  const initialOptions = Array.isArray(component.runtimeProperties.options)
    ? component.runtimeProperties.options.join('\n')
    : '';
  const initialCurrency = typeof component.runtimeProperties.currency === 'string'
    ? component.runtimeProperties.currency
    : 'USD';
  const nextControlled = controlledProperties(values.selection, values.optionsText, values.currency);
  const initialControlled = controlledProperties(initialSelection, initialOptions, initialCurrency);
  const controlledChanged = initialSelection !== values.selection
    || JSON.stringify(initialControlled) !== JSON.stringify(nextControlled);

  if (!hasLegacyPropertyDescriptors && controlledChanged) {
    const source = component.sourceProperties;
    const merged = source && typeof source === 'object' && !Array.isArray(source)
      ? { ...(source as Record<string, unknown>) }
      : {};
    CONTROLLED_PROPERTY_KEYS.forEach((key) => delete merged[key]);
    payload.properties = { ...merged, ...nextControlled };
  }
  // configurable is deliberately absent: this dialog has no configurable control.
  // Legacy descriptor arrays are read-only here because converting them to runtime
  // properties would destroy the descriptor schema.
  return payload;
}

const CreateComponentDialog: React.FC<CreateComponentDialogProps> = ({
  open, component = null, onClose, onSaved,
}) => {
  const { t } = useTranslation();
  const editing = Boolean(component);
  const hasLegacyPropertyDescriptors = Array.isArray(component?.sourceProperties);
  const [label, setLabel] = useState('');
  const [name, setName] = useState('');
  const [category, setCategory] = useState('Basic');
  const [description, setDescription] = useState('');
  const [selection, setSelection] = useState<ComponentTypeOption>('text_field');
  const [optionsText, setOptionsText] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const nextSelection = runtimeSelection(component);
    const properties = component?.runtimeProperties ?? {};
    setLabel(component?.label ?? '');
    setName(component?.name ?? '');
    setCategory(component?.category ?? 'Basic');
    setDescription(component?.description ?? '');
    setSelection(nextSelection);
    setOptionsText(Array.isArray(properties.options) ? properties.options.join('\n') : '');
    setCurrency(typeof properties.currency === 'string' ? properties.currency : 'USD');
    setError(null);
  }, [component, open]);

  const handleLabel = (next: string) => {
    setLabel(next);
    if (!editing) {
      setName((current) => (!current || current === deriveComponentName(label))
        ? deriveComponentName(next)
        : current);
    }
  };

  const handleClose = () => {
    if (!saving) onClose();
  };

  const handleSave = async () => {
    const slug = name.trim();
    if (!label.trim()) return setError(t('processes:createComponent.labelRequired'));
    if (!/^[a-z][a-z0-9_]{1,63}$/.test(slug)) return setError(t('processes:createComponent.slugInvalid'));
    if (!hasLegacyPropertyDescriptors
      && selection === 'select_dropdown'
      && controlledProperties(selection, optionsText, currency).options instanceof Array
      && (controlledProperties(selection, optionsText, currency).options as string[]).length === 0) {
      return setError(t('processes:createComponent.optionsRequired'));
    }
    setSaving(true);
    setError(null);
    const fieldType = selection === 'invoice_file' ? 'file_attachment' : selection;
    const editorValues = {
      name: slug,
      label: label.trim(),
      category,
      description,
      selection,
      optionsText,
      currency,
    };
    const createPayload = {
      name: slug,
      field_type: fieldType,
      label: label.trim(),
      category,
      description: description.trim() || null,
      properties: controlledProperties(selection, optionsText, currency),
      configurable: true,
    };
    try {
      if (component) {
        await processesApi.formComponentsApi.update(
          component.id,
          buildComponentUpdatePayload(component, editorValues),
        );
      } else {
        await processesApi.formComponentsApi.create(createPayload);
      }
      onSaved();
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiError
        ? translateApiError(caught, t)
        : t(editing ? 'processes:createComponent.updateFailed' : 'processes:createComponent.createFailed'));
    } finally {
      setSaving(false);
    }
  };

  const properties = controlledProperties(selection, optionsText, currency);
  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography component="span" variant="h6" sx={{ fontWeight: 600 }}>
          {t(editing ? 'processes:createComponent.editTitle' : 'processes:createComponent.title')}
        </Typography>
        <IconButton aria-label={t('common:actions.close')} onClick={handleClose} disabled={saving}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ py: 3 }}>
        <Stack spacing={2.5}>
          {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
          {/* Says what this actually is. `FormComponentCreate.field_type` is
              constrained to the runtime types the engine executes, so a "custom
              field" is a saved PRESET of an existing type — its own label, hint and
              options — never a new kind of input. The old title ("New custom field")
              promised the second thing, which is why the owner suspected the button
              was fake (audit D7). */}
          {!editing && (
            <Typography variant="body2" color="text.secondary">
              {t('processes:createComponent.whatItIs')}
            </Typography>
          )}
          <TextField label={t('processes:createComponent.displayLabel')} value={label}
            onChange={(event) => handleLabel(event.target.value)} fullWidth required size="small"
            placeholder={t('processes:createComponent.displayLabelPlaceholder')}
            helperText={t('processes:createComponent.displayLabelHelper')} />
          <TextField label={t('processes:createComponent.slugLabel')} value={name}
            onChange={(event) => setName(event.target.value.toLowerCase())} fullWidth required size="small"
            helperText={t('processes:createComponent.slugHelper')} />
          <FormControl fullWidth size="small">
            <InputLabel id="component-runtime-type-label">{t('processes:createComponent.behavesLike')}</InputLabel>
            <Select value={selection} label={t('processes:createComponent.behavesLike')}
              labelId="component-runtime-type-label"
              disabled={hasLegacyPropertyDescriptors}
              onChange={(event) => setSelection(event.target.value as ComponentTypeOption)}>
              {COMPONENT_TYPE_OPTIONS.map((type) => (
                <MenuItem key={type} value={type}>{t(`processes:createComponent.types.${type}`)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {(selection === 'select_dropdown' || selection === 'multi_select') && (
            <TextField label={t('processes:createComponent.optionsLabel')} value={optionsText}
              onChange={(event) => setOptionsText(event.target.value)} multiline minRows={3} required
              disabled={hasLegacyPropertyDescriptors}
              helperText={t('processes:createComponent.optionsHelper')} />
          )}
          {selection === 'currency_field' && (
            <TextField label={t('processes:createComponent.currencyLabel')} value={currency}
              onChange={(event) => setCurrency(event.target.value.toUpperCase().slice(0, 3))}
              disabled={hasLegacyPropertyDescriptors}
              inputProps={{ maxLength: 3 }} required size="small" />
          )}
          {(selection === 'file_attachment' || selection === 'invoice_file') && (
            <Alert severity="info">
              {t(selection === 'invoice_file'
                ? 'processes:createComponent.invoicePresetInfo'
                : 'processes:createComponent.attachmentPresetInfo', {
                  size: Math.round(Number(properties.max_bytes) / 1024 / 1024),
                })}
            </Alert>
          )}
          <FormControl fullWidth size="small">
            <InputLabel id="component-field-family-label">{t('processes:formComponents.fieldFamilyLabel')}</InputLabel>
            <Select value={category} label={t('processes:formComponents.fieldFamilyLabel')}
              labelId="component-field-family-label"
              onChange={(event) => setCategory(event.target.value)}>
              {FIELD_FAMILIES.map((family) => <MenuItem key={family} value={family}>{getFieldFamilyLabel(t, family)}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label={t('processes:createComponent.description')} value={description}
            onChange={(event) => setDescription(event.target.value)} fullWidth multiline rows={3} size="small"
            placeholder={t('processes:createComponent.descriptionPlaceholder')} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ py: 2 }}>
        <Button onClick={handleClose} disabled={saving}>{t('common:actions.cancel')}</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || !label.trim() || !name.trim()}>
          {saving ? <CircularProgress size={16} color="inherit" /> : t(editing ? 'common:actions.save' : 'common:actions.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateComponentDialog;
